import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Fish, Waves, Sparkles, Compass } from "lucide-react";
import Hero from "../components/Hero";
import ActivityCard from "../components/ActivityCard";
import { useLang } from "../context/LanguageContext";
import { activities } from "../data/activities";

const Home = () => {
  const { t, lang } = useLang();
  const featured = activities.slice(0, 6);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Intro */}
      <section className="section-pad bg-white" data-testid="intro-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">{t.intro.eyebrow}</p>
            <h2 className="h-display mt-4 text-3xl md:text-4xl lg:text-5xl font-light text-[#0B192C]">
              {t.intro.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-[17px] md:text-lg leading-relaxed text-[#475569] font-light">
              {t.intro.body}
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-[13px] font-semibold text-[#0B192C] border-b border-[#0B192C] pb-1 hover:gap-3 transition-all" data-testid="intro-about-link">
              {t.intro.cta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Stat strip */}
        <div className="container-x mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "68°N", v: t.extras.stat_arctic_lofoten },
            { k: "10.4m", v: t.extras.stat_viknes_vessel },
            { k: "10", v: t.extras.stat_max_guests },
            { k: "15+", v: t.extras.stat_activities },
          ].map((s, i) => (
            <div key={i} className="border-t border-black/10 pt-6">
              <p className="h-display text-3xl md:text-4xl text-[#0B192C] font-light">{s.k}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#94A3B8]">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="section-pad bg-[#F7F5F0]" data-testid="highlights-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="label-eyebrow">{t.highlights.eyebrow}</p>
              <h2 className="h-display mt-4 text-3xl md:text-4xl lg:text-5xl font-light text-[#0B192C]">
                {t.highlights.title}
              </h2>
              <p className="mt-5 text-[#475569] font-light text-lg">{t.highlights.subtitle}</p>
            </div>
            <Link to="/activities" className="btn-secondary w-fit" data-testid="highlights-see-all">
              {t.highlights.see_all} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(0,auto)]">
            {featured.map((a, i) => (
              <ActivityCard key={a.id} activity={a} variant={a.span} />
            ))}
          </div>
        </div>
      </section>

      {/* Feature banner */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden" data-testid="feature-banner">
        <img
          src="https://images.unsplash.com/photo-1656490247857-cfad59341c33"
          alt="Lofoten fjord"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B192C]/80 via-[#0B192C]/40 to-transparent" />
        <div className="relative z-10 container-x h-full flex items-center">
          <div className="max-w-xl text-white">
            <p className="label-eyebrow text-white/80">Viksund 340 St. Cruz</p>
            <h3 className="h-display mt-4 text-3xl md:text-5xl font-light">
              {t.extras.boat_banner_title}
            </h3>
            <p className="mt-5 text-white/80 font-light text-lg">
              {t.extras.boat_banner_body}
            </p>
            <Link to="/about" className="mt-8 inline-flex btn-ghost" data-testid="feature-about-link">
              {t.extras.boat_banner_cta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-pad bg-white" data-testid="testimonials-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="label-eyebrow">{t.testimonials.eyebrow}</p>
            <h2 className="h-display mt-4 text-3xl md:text-4xl lg:text-5xl font-light text-[#0B192C]">
              {t.testimonials.title}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.testimonials.items.map((it, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="rounded-2xl border border-black/5 bg-[#F7F5F0] p-8"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 text-[#0B192C]">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Sparkles key={j} size={12} />
                  ))}
                </div>
                <blockquote className="mt-6 text-[17px] leading-relaxed text-[#0B192C] font-light">
                  "{it.quote}"
                </blockquote>
                <figcaption className="mt-8 text-sm">
                  <span className="font-semibold text-[#0B192C]">{it.name}</span>
                  <span className="mx-2 text-[#CBD5E1]">·</span>
                  <span className="text-[#475569]">{it.country}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="bg-[#0B192C] text-white" data-testid="home-cta">
        <div className="container-x py-20 md:py-28 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h3 className="h-display text-3xl md:text-5xl font-light max-w-2xl">
            {t.extras.home_cta_title}
          </h3>
          <Link to="/booking" className="btn-primary bg-white text-[#0B192C] hover:bg-[#E5D9C5]" data-testid="cta-book-now">
            {t.nav.book_now} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
